import { DragEndEvent } from "@dnd-kit/core";
import { useMemo } from "react";
import { Col } from "react-bootstrap";
import { PlusCircle } from "react-bootstrap-icons";
import { SortableListHolder } from "../../controls";
import { defaultEditorDefns, EditorDefns, getFromDataPath } from "../../utility";
import { CaseWhenCondition, CaseWhenItem } from "../ars_ldm";
import { PATCH_ARS_STATE, useArsContext } from "../contexts";
import { isCaseWhenCondition } from "../typeGuards";
import * as styles from "./CaseWhen.module.css";
import { CaseWhenItemControl } from "./CaseWhenItemControl";

export interface CaseWhenControlProps<T> {
  id: string;
  dataPath: string;
  disabled?: boolean;
  editorDefns?: EditorDefns<T>;
  onChange?: (newValue?: CaseWhenCondition<T>) => void;
}

/**
 * Control for CaseWhen clause lists
 *
 * @param props - The props for the CaseWhenControl component
 * @param props.id - Unique identifier for the control
 * @param props.dataPath - The data path for this CaseWhenCondition
 * @param props.disabled - Optional boolean to disable the control
 * @param props.editorDefns - Optional type guard function for the generic type T used in CaseWhenItems
 * @param props.onChange - Optional callback function to handle changes to the where clause instead of dispatching to ArsContext
 * @returns A React component that renders a control for managing CaseWhen clauses
 */
export const CaseWhenControl = <T = string,>({
  id,
  dataPath,
  disabled,
  editorDefns = defaultEditorDefns as EditorDefns<T>,
  onChange,
}: CaseWhenControlProps<T>) => {
  const { state, dispatch } = useArsContext<T>();
  const thisCaseWhenCondition = useMemo<CaseWhenCondition<T> | undefined>(
    () =>
      getFromDataPath(dataPath, state, (obj) => isCaseWhenCondition(obj, editorDefns.isT)) ??
      (dataPath.split("/").slice(-2)[0] === "caseWhenConditions"
        ? // Create blank CaseWhenItem if the parent is a CaseWhenCondition and the item does not exist
          {
            id: dataPath.split("/").slice(-1)[0],
            caseWhenItems: [],
          }
        : undefined),
    [dataPath, editorDefns, state],
  );

  if (!thisCaseWhenCondition)
    return <span className="text-danger">Entity not found at path: {dataPath}</span>;

  /**
   * Handle variable reordering
   *
   * @param event - Sortable list drag end event
   */
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    /* istanbul ignore else */
    if (active.id !== over?.id) {
      const activeIndex = thisCaseWhenCondition.caseWhenItems.findIndex(
        (item) => item.id === active.id,
      );
      const overIndex = thisCaseWhenCondition.caseWhenItems.findIndex(
        (item) => item.id === over?.id,
      );
      const newItems = [...thisCaseWhenCondition.caseWhenItems];
      const [movedItem] = newItems.splice(activeIndex, 1);
      newItems.splice(overIndex, 0, movedItem);
      if (onChange) {
        onChange({ ...thisCaseWhenCondition, caseWhenItems: newItems });
      } else {
        dispatch({
          operation: PATCH_ARS_STATE,
          dataPath: `/caseWhenConditions/${thisCaseWhenCondition.id}/caseWhenItems`,
          value: newItems,
        });
      }
    }
  };

  /**
   * Add new item to the condition
   */
  const handleAddItem = () => {
    const newCaseWhenItem: CaseWhenItem<T> = {
      id: crypto.randomUUID(),
      displayPattern: state.editorDefns.blankT,
      resultPattern: "",
      when: null,
    };
    const newItems = [...thisCaseWhenCondition.caseWhenItems, newCaseWhenItem];
    if (onChange) {
      onChange({ ...thisCaseWhenCondition, caseWhenItems: newItems });
    } else {
      if (newItems.length === 1) {
        // If this is the first item, we need to create the parent condition as well
        dispatch({
          operation: PATCH_ARS_STATE,
          dataPath: `/caseWhenConditions/${thisCaseWhenCondition.id}`,
          value: {
            ...thisCaseWhenCondition,
            caseWhenItems: newItems,
          },
        });
      }
      dispatch({
        operation: PATCH_ARS_STATE,
        dataPath: `/caseWhenConditions/${thisCaseWhenCondition.id}/caseWhenItems`,
        value: newItems,
      });
    }
  };

  return (
    <Col className={styles.itemsHolder}>
      <SortableListHolder
        id={id}
        canMove={!disabled}
        handleDragEnd={handleDragEnd}
        items={thisCaseWhenCondition.caseWhenItems.map((v, ix) => ({
          id: v.id,
          element: (
            <CaseWhenItemControl<T>
              id={`${id}-item-${ix}`}
              dataPath={`${dataPath}/caseWhenItems/${ix}`}
              disabled={disabled}
              onChange={
                onChange
                  ? (ret) => {
                      const newItems = [...thisCaseWhenCondition.caseWhenItems];
                      const thisIx = newItems.findIndex((item) => item.id === v.id);
                      if (thisIx !== -1) {
                        if (ret) {
                          newItems[thisIx] = ret;
                        } else {
                          newItems.splice(thisIx, 1);
                        }
                        onChange({ ...thisCaseWhenCondition, caseWhenItems: newItems });
                      }
                    }
                  : undefined
              }
              siblingCount={thisCaseWhenCondition.caseWhenItems.length}
            />
          ),
        }))}
      />
      {!disabled && (
        <PlusCircle
          id={`${id}-add-item-button`}
          className="text-success"
          role="button"
          aria-label="Add item"
          onClick={handleAddItem}
          style={{ cursor: "pointer" }}
        />
      )}
    </Col>
  );
};

CaseWhenControl.displayName = "CaseWhenControl";
